import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
// import NProgress from 'nprogress';

import { authApi } from '@/utilities/axios';
import { useAuthStore } from '@/modules/auth/store';
import { showToast } from '@/utilities/toast-handler';
import { formatTreeData, formatTreeDatatable, formatVueTreeSelectData, flattenArray } from '../../../../../utilities/json-formatter';
import EventBus from '../../../../../utilities/event-bus';

const SOURCE_URL = process.env.VUE_APP_API_VERSION + '/sources';

export const useSourceStore = defineStore('source', () => {
    const authStore = useAuthStore();
    const company_id = computed(() => {
        return authStore.getLoggedUserCompanyId;
    });

    //state
    const source = ref({
        name: null,
        parent_id: null,
        company_id: null,
    });

    const sources = ref([]);
    const loading = ref(false);
    const editId = ref(null);

    // getters
    const sourceTreeTable = computed(() => {
        return formatTreeDatatable(sources.value);
    });
    const sourceTree = computed(() => {
        return formatTreeData(sources.value);
    });
    const sourceVueTree = computed(() => {
        return formatVueTreeSelectData(sources.value);
    });

    const flatSources = computed(() => {
        return flattenArray(sources.value);
    });

    //actions
    const fetchAllSources = async () => {
        try {
            // NProgress.start();
            loading.value = true;
            const response = await authApi.get(SOURCE_URL, {
                params: {
                    company_id: company_id.value,
                },
            });
            if (response.statusCode === 200) {
                sources.value = response.data;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch sources');
        } finally {
            // NProgress.done();
            loading.value = false;
        }
    };

    const createSource = async () => {
        try {
            EventBus.emit('show-operation-loader');
            const bodyParams = { ...source.value, company_id: company_id.value };
            const response = await authApi.post(SOURCE_URL, bodyParams);
            if (response.statusCode === 201) {
                fetchAllSources();
                showToast(response.toast, 'New source added successfully');
                return true;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to add source');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    const updateSource = async () => {
        try {
            EventBus.emit('show-operation-loader');
            const bodyParams = { ...source.value, company_id: company_id.value };
            const response = await authApi.patch(`${SOURCE_URL}/${editId.value}`, bodyParams);
            if (response.statusCode === 200) {
                fetchAllSources();
                showToast(response.toast, 'Source updated successfully');
                return true;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to update source');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    const deleteSource = async (sourceId) => {
        try {
            EventBus.emit('show-operation-loader');
            const response = await authApi.delete(`${SOURCE_URL}/${sourceId}`);
            if (response.statusCode === 200) {
                showToast(response.toast, 'Source deleted successfully');
                fetchAllSources();
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to delete source');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    const fetchSourceDetails = async (sourceId) => {
        try {
            const response = await authApi.get(`${SOURCE_URL}/${sourceId}`, {
                params: {
                    company_id: company_id.value,
                },
            });
            if (response.statusCode === 200) {
                return response.data;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch source details');
        }
    };

    // helpers
    const initSource = () => {
        source.value.name = null;
        source.value.parent_id = null;
        source.value.company_id = null;
        editId.value = null;
    };

    const createEditSourceBody = (editSource) => {
        editId.value = editSource?.key;
        source.value.name = editSource?.data?.name;
        source.value.parent_id = editSource?.data?.parent_id;
    };

    return {
        //state
        source,
        sources,
        editId,

        //getters
        sourceTree,
        sourceTreeTable,
        sourceVueTree,
        flatSources,
        //action
        fetchAllSources,
        createSource,
        updateSource,
        deleteSource,
        fetchSourceDetails,

        //helpers
        initSource,
        createEditSourceBody,
    };
});
